<script lang="ts" setup>
	/**
	 * Racine de l'application.
	 *
	 * Trois responsabilités, dans cet ordre :
	 *  1. résoudre la session et le mode d'affichage avant tout rendu — un écran
	 *     de borne ne doit jamais afficher fugitivement l'interface client ;
	 *  2. brancher le temps réel une fois le jeton connu ;
	 *  3. router les événements entrants vers les stores, quel que soit le canal
	 *     (WebSocket ou interrogation périodique).
	 */
	import { computed, onMounted, ref, watch } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import { useI18n } from 'vue-i18n';
	import { RealtimeEvent } from '@taxi/shared';
	import {
		ApiException,
		useRealtime,
		useTokenStore,
		useToastStore,
		useUserStore,
		useInterval,
		useNativeEvent,
		useVisibility,
	} from '@common/index';
	import Notify from '@common/components/Notify.vue';
	import { useAppModeStore } from '@/stores/AppModeStore';
	import { useRideStore } from '@/stores/RideStore';
	import { useDriverStore } from '@/stores/DriverStore';
	import { useAppUpdate } from '@/composables/useAppUpdate';
	import { useNativeBridge } from '@/composables/useNativeBridge';
	import AppShell from '@/components/layout/AppShell.vue';
	import SplashScreen from '@/components/layout/SplashScreen.vue';
	import OfflineBanner from '@/components/layout/OfflineBanner.vue';
	import OfferDialog from '@/components/driver/OfferDialog.vue';

	const route = useRoute();
	const router = useRouter();
	const { locale } = useI18n();

	const tokenStore = useTokenStore();
	const userStore = useUserStore();
	const appMode = useAppModeStore();
	const rideStore = useRideStore();
	const driverStore = useDriverStore();
	const toasts = useToastStore();
	const { realtime, on, onPoll } = useRealtime();
	const appUpdate = useAppUpdate();
	const native = useNativeBridge();

	const booting = ref(true);

	/**
	 * Une mise à jour ne s'applique jamais pendant une course.
	 *
	 * Recharger l'application ferait perdre l'écran de suivi au client, ou son
	 * écran de course au chauffeur en plein trajet. La bannière attend donc que
	 * la situation soit calme.
	 */
	const canUpdateNow = computed(
		() => appUpdate.updateAvailable.value && !rideStore.hasActiveRide && !driverStore.hasRide,
	);

	/**
	 * La mise à jour s'applique d'elle-même, sans rien demander.
	 *
	 * Une bannière suppose quelqu'un pour la lire et cliquer : ni la borne d'un
	 * hall d'hôtel ni le téléphone posé sur un tableau de bord n'ont ce
	 * quelqu'un, et l'application restait alors indéfiniment sur une version
	 * périmée. Le seul moment où l'on s'abstient est une course en cours —
	 * recharger ferait perdre son écran de suivi au client, son écran de course
	 * au chauffeur. C'est exactement ce que dit `canUpdateNow`.
	 */
	watch(canUpdateNow, (ready) => {
		if (ready) {
			void appUpdate.applyUpdate();
		}
	});

	/** La borne impose son thème et sa langue : elle ne suit pas les préférences locales. */
	const theme = computed(() => {
		if (appMode.isKiosk) {
			return 'monacoKiosk';
		}
		return appMode.isDriving ? 'monacoDark' : 'monacoLight';
	});

	const rootClass = computed(() => ({
		'app--kiosk': appMode.isKiosk,
		'app--driving': appMode.isDriving,
	}));

	/**
	 * Démarrage de l'application.
	 *
	 * Tout y est enveloppé : **aucune étape de démarrage ne doit pouvoir retenir
	 * l'écran d'accueil**. Un jeton périmé traînant dans le stockage local suffisait
	 * à faire échouer la restauration d'état, et l'exception laissait l'utilisateur
	 * devant l'écran de démarrage indéfiniment — sans message, sans issue, jusqu'à
	 * ce qu'il vide son navigateur.
	 */
	onMounted(async () => {
		try {
			tokenStore.setup();
			await userStore.load();
			await appMode.resolve();

			// La permission de localisation est demandée une fois le mode connu :
			// une borne n'en a aucun besoin, elle connaît sa position.
			if (!appMode.isKiosk) {
				await native.requestLocationPermission();
			}

			if (appMode.isKiosk && appMode.kiosk) {
				locale.value = appMode.kiosk.locale;
			}

			if (tokenStore.isAuthenticated) {
				try {
					await restoreState();
				} catch (error) {
					// Session inutilisable : on repart anonyme plutôt que de bloquer.
					// L'accueil rouvrira une session invité de lui-même.
					if (error instanceof ApiException && error.isUnauthorized) {
						tokenStore.setToken(null);
					}
					console.warn('État non restauré au démarrage :', error);
				}
			}

			await stripKioskParam();
			redirectToModeHome();
		} catch (error) {
			console.error('Démarrage incomplet :', error);
		} finally {
			booting.value = false;
		}
	});

	/**
	 * Connexion du canal temps réel, asservie au jeton.
	 *
	 * Se connecter une seule fois au démarrage ne suffit pas : une session anonyme
	 * n'est ouverte qu'à l'arrivée sur l'accueil, donc **après** ce démarrage. Le
	 * canal restait alors fermé pour tout client sans compte — l'immense majorité —
	 * et l'écran de suivi ne bougeait plus une fois la course commandée.
	 *
	 * Le jeton change aussi à la connexion, à la déconnexion et au rafraîchissement :
	 * la socket doit se rouvrir avec le nouveau, la passerelle l'ayant validé à
	 * l'établissement.
	 */
	watch(
		() => tokenStore.token?.accessToken ?? null,
		(accessToken, previous) => {
			if (!accessToken) {
				realtime.disconnect();
				return;
			}
			if (accessToken !== previous) {
				realtime.connect();
			}
		},
		{ immediate: true },
	);

	/**
	 * Rétablit l'état après un redémarrage.
	 *
	 * L'application chauffeur reste ouverte des heures et le navigateur peut la
	 * suspendre : à la reprise, on recharge la course en cours et les
	 * propositions en attente plutôt que d'attendre le prochain événement.
	 */
	const restoreState = async (): Promise<void> => {
		await rideStore.loadCurrent();
		if (appMode.isDriving) {
			await driverStore.loadSelf();
			await driverStore.loadOffers();
		}
	};

	/**
	 * Amène le chauffeur sur son écran de conduite.
	 *
	 * La borne, elle, n'a nulle part où être redirigée : elle utilise les mêmes
	 * écrans que tout le monde.
	 */
	/**
	 * Efface le jeton de borne de la barre d'adresse.
	 *
	 * Le magasin l'a déjà retiré par l'API History, mais le routeur termine sa
	 * navigation initiale avec l'adresse qu'il a mémorisée au démarrage — jeton
	 * compris — et la réécrit par-dessus. Le retrait doit donc repasser par lui
	 * une fois la navigation établie.
	 *
	 * Ce n'est pas cosmétique : un jeton laissé en clair sur un écran public se
	 * photographie, se recopie et finit dans un historique.
	 */
	const stripKioskParam = async (): Promise<void> => {
		// La navigation initiale du routeur réécrit l'adresse mémorisée au
		// démarrage — jeton compris. Nettoyer avant qu'elle s'achève ne servirait à
		// rien : elle remettrait le paramètre juste après.
		await router.isReady();

		if (route.query.kiosk === undefined) {
			return;
		}
		const { kiosk, ...rest } = route.query;
		await router.replace({ path: route.path, query: rest });
	};

	const redirectToModeHome = (): void => {
		if (appMode.isDriving && route.name === 'home') {
			void router.replace({ name: 'driver-home' });
		}
	};

	watch(() => appMode.mode, redirectToModeHome);

	// Retour au premier plan : l'application a pu manquer des événements en
	// veille, on resynchronise plutôt que d'afficher un état périmé.
	useVisibility({
		onVisible: () => {
			void appMode.retryKiosk();
			void driverStore.flushPending();
			if (tokenStore.isAuthenticated) {
				void restoreState();
			}
		},
	});

	// Retour du réseau : les étapes de course déclarées dans un tunnel partent
	// enfin. Sans cela, le client resterait sur « chauffeur en approche » alors
	// qu'il est déjà à bord.
	useNativeEvent(window, 'online', () => void driverStore.flushPending());

	// Une borne dont la session n'a pas pu s'ouvrir se rattrape d'elle-même :
	// l'écran est mural, personne ne viendra recharger la page.
	useInterval(() => void appMode.retryKiosk(), 30_000);

	// --- Réception des événements -------------------------------------------

	on(RealtimeEvent.RIDE_UPDATED, ({ ride }) => {
		rideStore.applyRideUpdate(ride);
		if (appMode.isDriving) {
			driverStore.applyRideUpdate(ride);
		}
	});

	on(RealtimeEvent.RIDE_DRIVER_POSITION, ({ rideId, position }) => {
		rideStore.applyDriverPosition(rideId, position);
	});

	on(RealtimeEvent.OFFER_RECEIVED, (payload) => {
		driverStore.receiveOffer(payload);
	});

	on(RealtimeEvent.OFFER_CLOSED, ({ offerId }) => {
		driverStore.closeOffer(offerId);
	});

	on(RealtimeEvent.NOTIFICATION, ({ notification }) => {
		toasts.info({ title: notification.title, message: notification.body ?? undefined });
	});

	/**
	 * La course commandée à la borne a été reprise sur un téléphone.
	 *
	 * L'écran peut revenir à son accueil : le voyageur a le suivi en main, et la
	 * borne redevient disponible pour le client suivant.
	 */
	on(RealtimeEvent.KIOSK_RIDE_CLAIMED, () => {
		if (appMode.isKiosk) {
			rideStore.reset();
			void router.replace({ name: 'home' });
		}
	});

	// Repli par interrogation : mêmes conséquences, un canal différent. Les vues
	// n'ont donc pas à savoir comment l'information leur est parvenue.
	onPoll(async () => {
		if (!tokenStore.isAuthenticated) {
			return;
		}
		await rideStore.loadCurrent();
		if (appMode.isDriving) {
			await driverStore.loadOffers();
		}
	});
</script>

<template>
	<v-app :theme="theme" :class="rootClass">
		<SplashScreen v-if="booting" />

		<template v-else>
			<OfflineBanner />

			<AppShell />

			<v-main :class="{ 'app-main--kiosk': appMode.isKiosk }">
				<router-view />
			</v-main>

			<!-- Proposition de course : superposée à tout écran du mode conduite,
			     le chauffeur ne doit pas avoir à naviguer pour répondre. -->
			<OfferDialog v-if="appMode.isDriving" />
		</template>

		<Notify :large="appMode.isKiosk" :location="appMode.isKiosk ? 'top' : 'top right'" />
	</v-app>
</template>

<style lang="scss">
	// La borne occupe tout l'écran : pas de barre, donc pas de décalage.
	.app-main--kiosk {
		--v-layout-top: 0px;
		padding-top: 0 !important;
	}
</style>
